import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";
import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { DetailedCitizenScoresIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensScoresIndicatorC3";

import { IndicatorEsfC3CitizensScoresFilters } from "./IndicatorEsfC3CitizensScoresFilters";
import { IndicatorEsfC3CitizensScoresTable } from "./IndicatorEsfC3CitizensScoresTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC3CitizensScoresProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensScores: Paginated<DetailedCitizenScoresIndicatorC3Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensScoresIndicatorC3: () => Promise<
		DetailedCitizenScoresIndicatorC3Data[]
	>;
};

export function IndicatorEsfC3CitizensScores({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensScores,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensScoresIndicatorC3,
}: Readonly<IndicatorEsfC3CitizensScoresProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const citizensScores = await handleGetAllDetailedCitizensScoresIndicatorC3();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(citizensScores).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC3CitizensScoresToGenericObjectArray(citizensScores);
			exportExcel(
				excelData,
				`Pontuações de cidadãos - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC3CitizensScoresToGenericObjectArray(
		citizensScores: DetailedCitizenScoresIndicatorC3Data[]
	) {
		return citizensScores.map(
			(citizenScores) =>
				({
					Cidadão: citizenScores.fullName,
					"Data de Nascimento": dayjs(citizenScores.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenScores.birthdate),
					Documento: citizenScores.cpf ?? citizenScores.cns ?? "",
					Equipe: citizenScores.healthTeam?.name ?? "",
					Ine: citizenScores.healthTeam?.ine ?? "",
					Microarea: citizenScores.area ?? "FA",
					Dum: dayjs(citizenScores.dumDate).format("DD/MM/YYYY"),
					Dpp: dayjs(citizenScores.dppDate).format("DD/MM/YYYY"),
					IG: citizenScores.gestationalAge ?? "",
					"1ª Consulta": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.firstAttendanceStatus
					),
					"Consultas pré natal": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.sevenPregnancyAttendancesStatus
					),
					"Aferições de PA": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.sevenBloodPressureMeasurementsStatus
					),
					"Peso/Altura": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.sevenWeightAndHeightMetricsRegistersStatus
					),
					Visitas: getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.threeAcsHomeVisitsStatus
					),
					Vacinações: getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.vaccinationStatus
					),
					"Procedimentos 1º trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.firstTrimesterProceduresStatus
					),
					"Procedimentos 3º trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.thirdTrimesterProceduresStatus
					),
					"Consultas puerperais": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.puerperalAttendanceStatus
					),
					"Visitas no período puerperal": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.puerperalAcsHomeVisitStatus
					),
					"Atendimento odontológico": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenScores.dentalAttendancesStatus
					),
					Pontos: citizenScores.score,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC3CitizensScoresFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC3CitizensScoresTable
							citizensScores={paginatedCitizensScores.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensScores?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensScores.pageSize}
								totalPages={paginatedCitizensScores.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
